export type FinanceDashboard = {
  organizationId: string;
  year: number;
  totals: {
    inflowMinor: number;
    outflowMinor: number;
    netMinor: number;
  };
  monthlySeries: Array<{
    competenceMonth: string;
    netMinor: number;
  }>;
  cumulativeMinor: number;
  baselineImport: { completedAt: string; status: FinanceImportTerminalStatus } | null;
};

const FINANCE_IMPORT_TERMINAL_STATUSES = [
  'succeeded',
  'idempotent-replay',
  'conflict',
  'hash-mismatch',
  'aborted',
  'rolled-back',
] as const;

type FinanceImportTerminalStatus = (typeof FINANCE_IMPORT_TERMINAL_STATUSES)[number];

function isFinanceImportTerminalStatus(value: unknown): value is FinanceImportTerminalStatus {
  return (
    typeof value === 'string' &&
    (FINANCE_IMPORT_TERMINAL_STATUSES as readonly string[]).includes(value)
  );
}

export type FinanceMovement = {
  id: string;
  competenceMonth: string;
  amountMinor: number;
  direction: 'inflow' | 'outflow';
  movementStatus: 'Actual' | 'Forecast';
  origin: 'baseline-import' | 'forecast-manual';
  category: string;
  description: string;
  economicRelevant: boolean;
  projectLabel: string | null;
};

type FinanceMovementList = {
  organizationId: string;
  year: number;
  movements: FinanceMovement[];
  availableCategories: string[];
  availableProjectLabels: string[];
};

export type FinanceState =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      // One dashboard, for the selected year. It used to be a record of all three years, which
      // existed for one reason: the cumulative balance was summed here from the three years' nets.
      // The server returns `cumulativeMinor` now, so the extra two requests bought nothing — and the
      // record shape was the only thing that made the page's data volume depend on how many years the
      // demo happened to span.
      dashboard: FinanceDashboard;
      movements: FinanceMovement[];
      availableCategories: string[];
      availableProjectLabels: string[];
      // A field on `ready`, NOT a fourth member of this union. The filter controls live in the
      // `ready` branch, so a distinct `refreshing` status would unmount them at the very click that
      // triggers the refetch — which is the bug this replaces.
      refreshing: boolean;
    };

export type DirectionFilter = 'all' | 'inflow' | 'outflow';
export type StatusFilter = 'all' | 'Actual' | 'Forecast';

export function isFinanceDashboard(value: unknown): value is FinanceDashboard {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<FinanceDashboard>;
  return (
    typeof candidate.organizationId === 'string' &&
    typeof candidate.year === 'number' &&
    typeof candidate.totals?.inflowMinor === 'number' &&
    typeof candidate.totals?.outflowMinor === 'number' &&
    typeof candidate.totals?.netMinor === 'number' &&
    Array.isArray(candidate.monthlySeries) &&
    // `cumulativeMinor` is required, not optional, and that is deliberate: the browser no longer
    // derives it, so a payload from a finance that predates this release would otherwise reach the
    // monetary tile as `undefined` and render as a blank or a NaN. Requiring it here turns that
    // deployment ordering mistake into the page's tested error state instead.
    typeof candidate.cumulativeMinor === 'number' &&
    isFinanceBaselineImport(candidate.baselineImport)
  );
}

function isFinanceBaselineImport(value: unknown): value is FinanceDashboard['baselineImport'] {
  // Null is a legitimate value — no import has finished yet — so `null` passes and only a malformed
  // object fails.
  if (value === null || value === undefined) {
    return value === null;
  }
  if (typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<NonNullable<FinanceDashboard['baselineImport']>>;
  return (
    typeof candidate.completedAt === 'string' && isFinanceImportTerminalStatus(candidate.status)
  );
}

export function isFinanceMovementList(value: unknown): value is FinanceMovementList {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<FinanceMovementList>;
  return (
    typeof candidate.organizationId === 'string' &&
    typeof candidate.year === 'number' &&
    Array.isArray(candidate.movements) &&
    // Mirrors the work its dashboard twin does. Leaving this guard alone while extending the other
    // would mean the page validates one payload and trusts the other, and the filter chips would be
    // fed from an array nothing had checked the shape of.
    Array.isArray(candidate.availableCategories) &&
    Array.isArray(candidate.availableProjectLabels)
  );
}

/**
 * The register's columns in render order, closing the last two attributes `FIN-FR-7` asks for:
 * `origin` was already on the row type and already selected by the server, read only to decide
 * whether a row is editable, and never shown; `economicRelevant` was returned and dropped.
 *
 * Declared as a list because the header row AND the empty-state `colSpan` both derive from it. That
 * `colSpan` was a hand-maintained literal that `SL-0054` moved from 6 to 7 and this change would
 * have had to move to 9; the deferral report flagged that nothing verified it. Deriving it removes the
 * drift instead of re-betting on care. A `<td>` added without its `<th>` is still possible, which is
 * what `renders exactly one cell per declared column on every register row` exists to catch.
 */
export const MOVEMENT_COLUMN_KEYS = [
  'month',
  'description',
  'category',
  'direction',
  'status',
  'origin',
  'projectLabel',
  'economicRelevant',
  'amount',
] as const;
